import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock } from "lucide-react";

export default function Contact({ siteInfo }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Vielen Dank! Wir melden uns in Kürze.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Fehler beim Senden. Bitte erneut versuchen.");
    } finally {
      setSending(false);
    }
  };

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-stone-900 text-sand-100">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-clay font-bold mb-6" data-testid="contact-overline">Kontakt</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-sand-100 leading-tight mb-8" data-testid="contact-headline">
              Lassen Sie uns über <em>Ihr Projekt</em> sprechen.
            </h2>
            <p className="text-sand-100/70 leading-relaxed mb-10 text-lg">
              Schreiben Sie uns – wir erstellen Ihnen gerne ein kostenloses und unverbindliches Angebot.
            </p>

            <ul className="space-y-6">
              <li className="flex gap-4" data-testid="contact-info-phone">
                <Phone className="w-5 h-5 text-clay flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-sand-100/50">Telefon</p>
                  <a href={`tel:${siteInfo?.phone}`} className="text-sand-100 hover:text-clay-light transition-colors">{siteInfo?.phone}</a>
                </div>
              </li>
              <li className="flex gap-4" data-testid="contact-info-email">
                <Mail className="w-5 h-5 text-clay flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-sand-100/50">E-Mail</p>
                  <a href={`mailto:${siteInfo?.email}`} className="text-sand-100 hover:text-clay-light transition-colors">{siteInfo?.email}</a>
                </div>
              </li>
              <li className="flex gap-4" data-testid="contact-info-address">
                <MapPin className="w-5 h-5 text-clay flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-sand-100/50">Adresse</p>
                  <p className="text-sand-100">{siteInfo?.address}</p>
                </div>
              </li>
              <li className="flex gap-4" data-testid="contact-info-hours">
                <Clock className="w-5 h-5 text-clay flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-sand-100/50">Öffnungszeiten</p>
                  <p className="text-sand-100">{siteInfo?.business_hours}</p>
                </div>
              </li>
            </ul>
          </motion.div>

          {/* Right: Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            data-testid="contact-form"
            className="lg:col-span-7 bg-stone-800 p-8 sm:p-10 rounded-sm border border-stone-700"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-sand-100 mb-2 block">Name *</Label>
                <Input id="name" required value={form.name} onChange={update("name")} data-testid="contact-input-name" className="bg-stone-900 border-stone-700 text-sand-100" />
              </div>
              <div>
                <Label htmlFor="email" className="text-sand-100 mb-2 block">E-Mail *</Label>
                <Input id="email" type="email" required value={form.email} onChange={update("email")} data-testid="contact-input-email" className="bg-stone-900 border-stone-700 text-sand-100" />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sand-100 mb-2 block">Telefon</Label>
                <Input id="phone" value={form.phone} onChange={update("phone")} data-testid="contact-input-phone" className="bg-stone-900 border-stone-700 text-sand-100" />
              </div>
              <div>
                <Label htmlFor="subject" className="text-sand-100 mb-2 block">Betreff</Label>
                <Input id="subject" value={form.subject} onChange={update("subject")} data-testid="contact-input-subject" className="bg-stone-900 border-stone-700 text-sand-100" />
              </div>
            </div>
            <div className="mt-5">
              <Label htmlFor="message" className="text-sand-100 mb-2 block">Nachricht *</Label>
              <Textarea id="message" rows={6} required value={form.message} onChange={update("message")} data-testid="contact-input-message" className="bg-stone-900 border-stone-700 text-sand-100" />
            </div>
            <label className="mt-5 flex items-start gap-3 text-sm text-sand-100/70 cursor-pointer" data-testid="contact-privacy-consent">
              <input type="checkbox" required data-testid="contact-privacy-checkbox" className="mt-1 h-4 w-4 accent-clay" />
              <span>Ich habe die <Link to="/datenschutz" className="text-clay hover:underline">Datenschutzerklärung</Link> gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu. *</span>
            </label>
            <Button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="mt-8 bg-clay hover:bg-clay-hover text-sand-100 px-8 h-12 rounded-sm transition-colors duration-300"
            >
              {sending ? "Wird gesendet…" : "Anfrage senden"}
            </Button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
